import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../common/Modal";

const NewChatModal = ({
  isOpen,
  onClose,
  onCreateGeneralChat,
  onCreateProposalChat,
  proposals = [],
}) => {
  const [step, setStep] = useState("select"); // "select" or "proposal"
  const [selectedProposal, setSelectedProposal] = useState("");

  // Reset modal state when opened/closed
  React.useEffect(() => {
    if (isOpen) {
      setStep("select");
      setSelectedProposal("");
    }
  }, [isOpen]);

  const handleGeneralChatClick = () => {
    onCreateGeneralChat();
    onClose();
  };

  const handleProposalChatClick = () => {
    if (proposals.length === 0) {
      // If no proposals available, show a message or create general chat
      alert("No active proposals available. Creating a general chat instead.");
      handleGeneralChatClick();
      return;
    }
    setStep("proposal");
  };

  const handleProposalSelection = () => {
    if (selectedProposal) {
      onCreateProposalChat(parseInt(selectedProposal));
      onClose();
    }
  };

  const handleBack = () => {
    setStep("select");
    setSelectedProposal("");
  };

  // Filter active proposals
  const activeProposals = proposals.filter((p) => p.status === "Active");

  if (!isOpen) return null;

  return (
    <Modal
      title={step === "select" ? "Start New Chat" : "Choose Proposal"}
      onClose={onClose}
      hideFooter={true}
      widthClassName="max-w-lg"
      showCloseX={true}
      closeOnBackdropClick={true}
    >
      {step === "select" ? (
        <div className="space-y-6">
          {/* Description */}
          <p className="text-slate-600 text-center">
            What type of chat would you like to start?
          </p>

          {/* Chat Type Options */}
          <div className="space-y-4">
            {/* General Chat Option */}
            <button
              onClick={handleGeneralChatClick}
              className="w-full p-4 border-2 border-emerald-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-all duration-200 group"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors duration-200">
                  <FontAwesomeIcon
                    icon="robot"
                    className="text-emerald-600 text-xl"
                  />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    General Chat
                  </h3>
                  <p className="text-sm text-slate-600">
                    Ask general questions about proposal development, best
                    practices, or get help with writing and strategy.
                  </p>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="text-slate-400 group-hover:text-emerald-600 transition-colors duration-200"
                />
              </div>
            </button>

            {/* Proposal Chat Option */}
            <button
              onClick={handleProposalChatClick}
              disabled={activeProposals.length === 0}
              className="w-full p-4 border-2 border-orange-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-orange-200 disabled:hover:bg-white"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors duration-200">
                  <FontAwesomeIcon
                    icon="file"
                    className="text-orange-600 text-xl"
                  />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    Proposal Chat
                  </h3>
                  <p className="text-sm text-slate-600">
                    Chat about a specific proposal with access to all related
                    documents, requirements, and context.
                  </p>
                  {activeProposals.length === 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      No active proposals available
                    </p>
                  )}
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="text-slate-400 group-hover:text-orange-600 transition-colors duration-200"
                />
              </div>
            </button>
          </div>

          {/* Help Text */}
          <div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-2">
              <FontAwesomeIcon
                icon="info-circle"
                className="text-blue-600 text-sm mt-0.5 flex-shrink-0"
              />
              <p className="text-xs text-blue-700">
                <strong>Tip:</strong> Proposal chats have access to all uploaded
                documents and can provide context-specific assistance. General
                chats are great for broader questions and guidance.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-slate-600 hover:text-slate-800 transition-colors duration-200"
          >
            <FontAwesomeIcon icon="chevron-left" className="text-sm" />
            <span className="text-sm">Back to chat types</span>
          </button>

          {/* Description */}
          <p className="text-slate-600">
            Select the proposal you'd like to chat about:
          </p>

          {/* Proposal Selection */}
          <div className="space-y-3">
            <select
              value={selectedProposal}
              onChange={(e) => setSelectedProposal(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Choose a proposal...</option>
              {activeProposals.map((proposal) => (
                <option key={proposal.proposal_id} value={proposal.proposal_id}>
                  {proposal.proposal_name}
                  {proposal.customer_name && ` - ${proposal.customer_name}`}
                </option>
              ))}
            </select>

            {/* Proposal Info */}
            {selectedProposal && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                {(() => {
                  const proposal = activeProposals.find(
                    (p) => p.proposal_id === parseInt(selectedProposal)
                  );
                  return proposal ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon
                          icon="file"
                          className="text-orange-600"
                        />
                        <span className="font-medium text-slate-900">
                          {proposal.proposal_name}
                        </span>
                      </div>
                      {proposal.customer_name && (
                        <p className="text-sm text-slate-600">
                          Customer: {proposal.customer_name}
                        </p>
                      )}
                      {proposal.due_date && (
                        <p className="text-sm text-slate-600">
                          Due:{" "}
                          {new Date(proposal.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleBack}
              className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleProposalSelection}
              disabled={!selectedProposal}
              className="px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-orange-500"
            >
              Start Proposal Chat
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default NewChatModal;
